import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react'
import { NextIntlClientProvider, IntlConfig } from 'next-intl'
import edgeLogger from '@reward-platform/logger/edge'
import { createDatadogLogger } from '~/utils/logger'
import { PlatformError } from '~/utils/errors'
import { isClient } from '~/utils/envChecks'
import { DEFAULT_LOCALE } from '~/i18n/config'
import {
  LocalisationContextValue,
  MessageClusterState,
  LocalisationProviderProps,
} from './localisation.types'
import {
  formatRichMessageForKey,
  getCluster,
  mergeMessagesWithDefaults,
} from './Localisation.utils'
import { usePlatformConfig } from '../platform'

import defaultMessages from '../../i18n/defaultMessages/messages.en.json'
import legacyDefaultMessages from '../../i18n/defaultMessages/messages.legacy.json'

const LocalisationContext = createContext<LocalisationContextValue | undefined>(undefined)

LocalisationContext.displayName = 'LocalisationContext'

const isClusterEmpty = (messages?: MessageClusterState['messages']) =>
  messages && !Object.keys(messages)

export function LocalisationProvider({
  children,
  initialClusterKeys,
  initialMessages = {},
  shouldLogErrors = true,
  locale = DEFAULT_LOCALE,
  ...props
}: LocalisationProviderProps): React.ReactElement<React.PropsWithChildren> {
  const staticDefaultMessages = { ...defaultMessages, ...legacyDefaultMessages }
  // Track initial render
  const isInitialRender = useRef(true)

  // create store for dynamically requested clusters and messages
  const [clusterState, setClusterState] = useState<MessageClusterState>({
    messages: {},
    clusterKeys: new Set(),
  })

  const { opCo } = usePlatformConfig()

  // Helper for requesting an additional cluster dynamically
  const requestCollection = useCallback(
    async (clusterKey: string) => {
      // Don't request clusters already in state
      if (clusterState.clusterKeys.has(clusterKey)) {
        return
      }

      const messagesFromCluster = await getCluster(opCo, clusterKey, locale)

      // Update dynamic collection with new clusters
      setClusterState(({ clusterKeys, messages: currentMessages }) => ({
        clusterKeys: clusterKeys.add(clusterKey),
        // Deep merge so clusters sharing a top-level namespace (e.g. `pages`)
        // don't clobber each other's nested keys. The incoming cluster wins
        // leaf conflicts, matching the previous shallow-spread precedence.
        messages: mergeMessagesWithDefaults(messagesFromCluster, currentMessages),
      }))
    },
    [clusterState.clusterKeys, locale, opCo]
  )

  const handleErrors: IntlConfig['onError'] = (error) => {
    const description = 'IntlError'
    const errorMessage = `${description}: ${error.code}`
    const errorContext = { error: { ...error, description } }
    if (shouldLogErrors) {
      if (isClient()) {
        const logger = createDatadogLogger('intl-messages-error', { level: 'warn' })
        logger.warn(errorMessage, errorContext, error)
      } else {
        edgeLogger.warn(
          `${description}: ${error.code ?? 'unknown'} - ${
            error.message ?? 'Error with default server messages, check JSON'
          }`,
          { ...errorContext, code: error?.code, message: error?.message }
        )
      }
    }
  }

  const getMessageFallback: IntlConfig['getMessageFallback'] = ({
    error,
    key: failedKey,
    namespace: failedNamespace,
  }) => {
    const key = [failedNamespace, failedKey].filter(Boolean).join('.')
    return formatRichMessageForKey(key, staticDefaultMessages)
  }

  const contextValue = useMemo<LocalisationContextValue>(() => ({ locale }), [locale])

  // Load initial clusters on initial render
  useEffect(() => {
    if (isInitialRender.current && !clusterState.clusterKeys.size) {
      initialClusterKeys?.forEach(requestCollection)
    }
    isInitialRender.current = false
  }, [initialClusterKeys, requestCollection, clusterState])

  const messages = mergeMessagesWithDefaults(clusterState.messages, {
    ...staticDefaultMessages,
    ...initialMessages,
  })

  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      onError={handleErrors}
      getMessageFallback={getMessageFallback}
    >
      <LocalisationContext.Provider value={contextValue} {...props}>
        {children}
      </LocalisationContext.Provider>
    </NextIntlClientProvider>
  )
}

export function useLocalisation(): LocalisationContextValue {
  const context = useContext(LocalisationContext)
  if (!context) {
    throw new PlatformError('useLocalisation must be used within a LocalisationProvider')
  }
  return context
}
